import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { MeshBVH, SAH } from 'three-mesh-bvh';
import { getCadDiffGPU, FikoCadDiffGPU } from './FikoCadDiffGPU';
import { getVisualDiffGPU, FikoCadDiffVisualGPU } from './FikoCadDiffVisualGPU';
import { getCadDiffWorkerPool, FikoCadDiffWorkerPool } from './FikoCadDiffWorkerPool';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * FikoCAD Diff Service - CATIA V5 Tarzı CAD Karşılaştırma
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * CATIA V5 "Compare Products" / "Visual Comparison" benzeri:
 *
 * GEOMETRIC COMPARISON (Morton Code + Spatial Hash):
 * - Nokta bazlı hassas karşılaştırma
 * - O(n) with spatial hashing
 * - Küçük/orta modeller için ideal
 *
 * VISUAL COMPARISON (Jump Flood Algorithm):
 * - Distance field bazlı ultra hızlı
 * - O(n + m*log(grid)) with JFA
 * - Büyük modeller için ideal (100M+ vertex)
 *
 * Renkler:
 * - Farklı bölgeler → KIRMIZI
 * - Aynı bölgeler → SARI/GRİ
 *
 * @author FikoYT
 * @version 4.0.0 - Dual Mode (Geometric + Visual)
 */

// ═══════════════════════════════════════════════════════════════════════════════
// TİPLER
// ═══════════════════════════════════════════════════════════════════════════════

export type ComparisonMode = 'geometric' | 'visual';
export type ComparisonType = 'added_removed' | 'added_only' | 'removed_only';
export type ViewMode = 'both' | 'old_only' | 'new_only';

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface CadDiffColors {
  same: RGB;      // Aynı kısımlar (sarı)
  removed: RGB;   // Silinen (kırmızı)
  added: RGB;     // Eklenen (yeşil)
}

export interface DiffModelSecim {
  uuid: string;
  isim: string;
  nesne: THREE.Object3D;
  kaynak: 'agac' | 'sahne' | 'dosya';
}

export interface CadDiffOptions {
  mode: ComparisonMode;
  computationAccuracy: number;  // mm
  displayAccuracy: number;      // mm (for geometric)
  comparisonType: ComparisonType;
  viewMode: ViewMode;
  useLocalAxis: boolean;
  colors?: CadDiffColors;       // Özelleştirilebilir renkler
  showEdges?: boolean;          // Edge'leri göster
  edgeColor?: RGB;              // Edge rengi
  useHeatmapGradient?: boolean; // Gradient heatmap (default: false → eski binary renklendirme)
  useWorkerPool?: boolean;      // Worker pool kullan (default: true)
  refineBoundary?: boolean;     // Sınır üçgenlerini subdivide et (default: true)
  showOnlyDifferences?: boolean; // Sadece farklı bölgeleri göster (default: false)
  surfaceFilterMultiplier?: number;  // Tessellation artifact filtre çarpanı (default: 10, tolerance * N)
  subdivisionDepth?: number;         // Sınır üçgen subdivision derinliği (default: 3 onlyDiffs, 1 normal)
  ghostOpacity?: number;             // Ghost mode opaklığı (default: 0.15)
}

export interface CadDiffSonuc {
  meshA: THREE.BufferGeometry | null;
  meshB: THREE.BufferGeometry | null;
  edgesA: THREE.BufferGeometry | null;  // Edge geometry for A
  edgesB: THREE.BufferGeometry | null;  // Edge geometry for B
  originalGeoA?: THREE.BufferGeometry | null;  // Orijinal A geometry (dialog için)
  originalGeoB?: THREE.BufferGeometry | null;  // Orijinal B geometry (dialog için)
  maxSapma: number;
  ortalamaSapma: number;
  stdSapma: number;
  toleransIcindeYuzde: number;
  sure: number;
  vertexSayisiA: number;
  vertexSayisiB: number;
  renkSkalasi: RenkSkalasi;
  tier: 'gpu' | 'worker' | 'cpu';
  farkliVertexA: number;
  farkliVertexB: number;
  distancesA?: Float32Array;   // Her vertex için en yakın mesafe (gradient heatmap için)
  distancesB?: Float32Array;
}

export interface RenkSkalasi {
  min: number;
  max: number;
  tolerans: number;
}

export interface ProgressCallback {
  (progress: number, message: string): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// OPTİMİZE SPATIAL HASH - Chunk-based non-blocking
// ═══════════════════════════════════════════════════════════════════════════════

class SpatialHash3D {
  private readonly cellSize: number;
  private readonly invCellSize: number;
  private readonly cells: Map<number, Float32Array>;
  private readonly cellCounts: Map<number, number>;

  constructor(cellSize: number) {
    this.cellSize = cellSize;
    this.invCellSize = 1.0 / cellSize;
    this.cells = new Map();
    this.cellCounts = new Map();
  }

  private hash(x: number, y: number, z: number): number {
    const ix = Math.floor(x * this.invCellSize);
    const iy = Math.floor(y * this.invCellSize);
    const iz = Math.floor(z * this.invCellSize);
    return ((ix * 73856093) ^ (iy * 19349663) ^ (iz * 83492791)) >>> 0;
  }

  build(points: Float32Array): void {
    const pointCount = points.length / 3;

    for (let i = 0; i < pointCount; i++) {
      const idx = i * 3;
      const h = this.hash(points[idx], points[idx + 1], points[idx + 2]);
      this.cellCounts.set(h, (this.cellCounts.get(h) || 0) + 1);
    }

    for (const [h, count] of this.cellCounts) {
      this.cells.set(h, new Float32Array(count * 3));
      this.cellCounts.set(h, 0);
    }

    for (let i = 0; i < pointCount; i++) {
      const idx = i * 3;
      const x = points[idx];
      const y = points[idx + 1];
      const z = points[idx + 2];
      const h = this.hash(x, y, z);
      const cellIdx = this.cellCounts.get(h)! * 3;
      const cell = this.cells.get(h)!;
      cell[cellIdx] = x;
      cell[cellIdx + 1] = y;
      cell[cellIdx + 2] = z;
      this.cellCounts.set(h, this.cellCounts.get(h)! + 1);
    }
  }

  hasPointWithin(x: number, y: number, z: number, tolerance: number): boolean {
    const tolSq = tolerance * tolerance;
    const baseIx = Math.floor(x * this.invCellSize);
    const baseIy = Math.floor(y * this.invCellSize);
    const baseIz = Math.floor(z * this.invCellSize);

    const searchRadius = Math.max(1, Math.ceil(tolerance / this.cellSize));
    const limit = Math.min(searchRadius, 2);

    for (let dx = -limit; dx <= limit; dx++) {
      for (let dy = -limit; dy <= limit; dy++) {
        for (let dz = -limit; dz <= limit; dz++) {
          const h = this.hashFromInts(baseIx + dx, baseIy + dy, baseIz + dz);
          const cell = this.cells.get(h);

          if (cell) {
            for (let i = 0; i < cell.length; i += 3) {
              const ddx = x - cell[i];
              const ddy = y - cell[i + 1];
              const ddz = z - cell[i + 2];
              const distSq = ddx * ddx + ddy * ddy + ddz * ddz;
              if (distSq <= tolSq) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }

  findNearest(x: number, y: number, z: number, maxDist: number): number {
    const baseIx = Math.floor(x * this.invCellSize);
    const baseIy = Math.floor(y * this.invCellSize);
    const baseIz = Math.floor(z * this.invCellSize);

    let minDistSq = maxDist * maxDist;
    const limit = Math.min(Math.ceil(maxDist / this.cellSize), 3);

    for (let dx = -limit; dx <= limit; dx++) {
      for (let dy = -limit; dy <= limit; dy++) {
        for (let dz = -limit; dz <= limit; dz++) {
          const h = this.hashFromInts(baseIx + dx, baseIy + dy, baseIz + dz);
          const cell = this.cells.get(h);

          if (cell) {
            for (let i = 0; i < cell.length; i += 3) {
              const ddx = x - cell[i];
              const ddy = y - cell[i + 1];
              const ddz = z - cell[i + 2];
              const distSq = ddx * ddx + ddy * ddy + ddz * ddz;
              if (distSq < minDistSq) {
                minDistSq = distSq;
              }
            }
          }
        }
      }
    }

    return Math.sqrt(minDistSq);
  }

  private hashFromInts(ix: number, iy: number, iz: number): number {
    return ((ix * 73856093) ^ (iy * 19349663) ^ (iz * 83492791)) >>> 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANA SERVİS
// ═══════════════════════════════════════════════════════════════════════════════

export class FikoCadDiffService {
  // CATIA V5 Professional Color Palette
  private static readonly DEFAULT_COLORS: CadDiffColors = {
    same: { r: 0.78, g: 0.72, b: 0.32 },    // Warm gold - aynı (CATIA V5 tarzı)
    removed: { r: 0.82, g: 0.18, b: 0.32 },  // Coral-magenta - removed (sadece A'da)
    added: { r: 0.18, g: 0.52, b: 0.82 }     // Steel blue - added (sadece B'de)
  };

  private static readonly DEFAULT_EDGE_COLOR: RGB = { r: 0.15, g: 0.15, b: 0.15 };

  private options: CadDiffOptions = {
    mode: 'geometric',
    computationAccuracy: 1.0,
    displayAccuracy: 1.0,
    comparisonType: 'added_removed',
    viewMode: 'both',
    useLocalAxis: false,
    colors: { ...FikoCadDiffService.DEFAULT_COLORS },
    showEdges: true,
    edgeColor: { ...FikoCadDiffService.DEFAULT_EDGE_COLOR },
    useHeatmapGradient: false,
    useWorkerPool: true,
    refineBoundary: true,
    showOnlyDifferences: false
  };

  private cellSize = 1.0;
  private aborted = false;
  private _modelDiagonal = 1000; // Bounding box diagonal (mesafe clamping için)

  // Geometric Comparison GPU (Morton Code + Spatial Hash)
  private gpuService: FikoCadDiffGPU | null = null;
  private gpuInitialized = false;

  // Visual Comparison GPU (Jump Flood Algorithm)
  private visualGpuService: FikoCadDiffVisualGPU | null = null;
  private visualGpuInitialized = false;

  // Worker Pool (multi-core CPU paralel)
  private workerPool: FikoCadDiffWorkerPool | null = null;
  private workerPoolInitialized = false;

  private readonly EXCLUDED_NAMES = [
    'stok', 'stock', 'blank', 'raw', 'kutuk', 'kütük',
    'billet', 'workpiece', 'material', 'block', 'grid', 'helper'
  ];

  constructor() {}

  // Renk getter'ları (options'dan oku)
  private get COLOR_SAME(): RGB {
    return this.options.colors?.same || FikoCadDiffService.DEFAULT_COLORS.same;
  }
  private get COLOR_REMOVED(): RGB {
    return this.options.colors?.removed || FikoCadDiffService.DEFAULT_COLORS.removed;
  }
  private get COLOR_ADDED(): RGB {
    return this.options.colors?.added || FikoCadDiffService.DEFAULT_COLORS.added;
  }

  setOptions(opts: Partial<CadDiffOptions>): void {
    this.options = { ...this.options, ...opts };
  }

  getOptions(): CadDiffOptions {
    return { ...this.options };
  }

  setTolerans(toleransMM: number): void {
    this.options.computationAccuracy = Math.max(0.001, toleransMM);
  }

  abort(): void {
    this.aborted = true;
  }

  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * ASYNC KARŞILAŞTIRMA - GPU -> CPU Fallback
   * ═══════════════════════════════════════════════════════════════════════════
   */
  async karsilastir(
    nesneA: THREE.Object3D,
    nesneB: THREE.Object3D,
    onProgress?: ProgressCallback
  ): Promise<CadDiffSonuc> {
    this.aborted = false;
    const baslangic = performance.now();

    onProgress?.(0, 'Modeller hazırlanıyor...');
    await this.yieldToMain();

    // 1. World matrix güncelle
    nesneA.updateMatrixWorld(true);
    nesneB.updateMatrixWorld(true);

    // 2. Mesh'leri topla
    const meshlerA = this.meshTopla(nesneA);
    const meshlerB = this.meshTopla(nesneB);


    onProgress?.(5, 'Vertex verileri çıkarılıyor...');
    await this.yieldToMain();

    // 3. World-space vertex'leri çıkar
    const pointsA = this.extractWorldPoints(meshlerA);
    const pointsB = this.extractWorldPoints(meshlerB);

    // 3.5. Yerel eksen sistemi aktifse, her iki modeli kendi centroid'ine normalize et
    // Bu sayede farklı pozisyonlardaki aynı geometri doğru eşleşir
    let centroidA: { x: number; y: number; z: number } | undefined;
    let centroidB: { x: number; y: number; z: number } | undefined;
    if (this.options.useLocalAxis) {
      centroidA = this.normalizeToCenter(pointsA);
      centroidB = this.normalizeToCenter(pointsB);
      console.log(`[FikoCadDiff] Yerel eksen normalizasyonu: A centroid=(${centroidA.x.toFixed(2)}, ${centroidA.y.toFixed(2)}, ${centroidA.z.toFixed(2)}), B centroid=(${centroidB.x.toFixed(2)}, ${centroidB.y.toFixed(2)}, ${centroidB.z.toFixed(2)})`);
    }

    const vertexCountA = pointsA.length / 3;
    const vertexCountB = pointsB.length / 3;


    if (vertexCountA === 0 || vertexCountB === 0) {
      return this.bosSonuc(performance.now() - baslangic);
    }

    const tolerance = this.options.computationAccuracy;

    // Model bounding box diagonal hesapla (mesafe clamping için)
    const boundsA = this.calculateBounds(pointsA);
    const boundsB = this.calculateBounds(pointsB);
    const allMinX = Math.min(boundsA.min.x, boundsB.min.x);
    const allMinY = Math.min(boundsA.min.y, boundsB.min.y);
    const allMinZ = Math.min(boundsA.min.z, boundsB.min.z);
    const allMaxX = Math.max(boundsA.max.x, boundsB.max.x);
    const allMaxY = Math.max(boundsA.max.y, boundsB.max.y);
    const allMaxZ = Math.max(boundsA.max.z, boundsB.max.z);
    const dx = allMaxX - allMinX, dy = allMaxY - allMinY, dz = allMaxZ - allMinZ;
    this._modelDiagonal = Math.sqrt(dx * dx + dy * dy + dz * dz);

    let isSameA: boolean[];
    let isSameB: boolean[];
    let distancesA: Float32Array | undefined;
    let distancesB: Float32Array | undefined;
    let tier: 'gpu' | 'worker' | 'cpu' = 'cpu';

    // ═══════════════════════════════════════════════════════════════════════════
    // BVH OLUŞTUR (vertex-to-surface karşılaştırma için)
    // GPU/Worker vertex-to-vertex yapar, sonra BVH ile düzeltme yapılır.
    // CPU doğrudan BVH kullanır (en doğru sonuç).
    // ═══════════════════════════════════════════════════════════════════════════
    onProgress?.(7, 'BVH ağacı oluşturuluyor...');
    await this.yieldToMain();

    const bvhA = this.buildBVHFromMeshes(meshlerA, centroidA);
    const bvhB = this.buildBVHFromMeshes(meshlerB, centroidB);

    if (bvhA && bvhB) {
      console.log('[FikoCadDiff] BVH oluşturuldu: vertex-to-surface karşılaştırma aktif');
    } else {
      console.warn('[FikoCadDiff] BVH oluşturulamadı, vertex-to-vertex fallback kullanılacak');
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // MODE SELECTION: Visual (JFA) vs Geometric (Spatial Hash)
    // ═══════════════════════════════════════════════════════════════════════════

    if (this.options.mode === 'visual') {
      // ─────────────────────────────────────────────────────────────────────────
      // VISUAL COMPARISON - Jump Flood Algorithm (O(log n) distance field)
      // Büyük modeller için ideal (100M+ vertex destekler)
      // ─────────────────────────────────────────────────────────────────────────
      onProgress?.(10, 'Visual GPU (JFA) başlatılıyor...');

      const visualResult = await this.tryVisualGPUCompare(pointsA, pointsB, tolerance, onProgress);

      if (visualResult) {
        tier = 'gpu';
        isSameA = Array.from(visualResult.isSameA).map(v => v === 1);
        isSameB = Array.from(visualResult.isSameB).map(v => v === 1);
        // Visual GPU henüz distance döndürmüyor
      } else {
        // Visual GPU failed, fallback to geometric GPU
        const gpuResult = await this.tryGPUCompare(pointsA, pointsB, tolerance, onProgress);

        if (gpuResult) {
          tier = 'gpu';
          isSameA = Array.from(gpuResult.isSameA).map(v => v === 1);
          isSameB = Array.from(gpuResult.isSameB).map(v => v === 1);
          distancesA = gpuResult.distancesA;
          distancesB = gpuResult.distancesB;
        } else if (this.options.useWorkerPool !== false) {
          // Worker Pool fallback
          const workerResult = await this.tryWorkerCompare(pointsA, pointsB, tolerance, onProgress);
          if (workerResult) {
            tier = 'worker';
            isSameA = workerResult.isSameA;
            isSameB = workerResult.isSameB;
            distancesA = workerResult.distancesA;
            distancesB = workerResult.distancesB;
          } else {
            // Final fallback to CPU (BVH ile)
            const cpuResult = await this.cpuFallback(pointsA, pointsB, tolerance, onProgress, bvhA, bvhB);
            isSameA = cpuResult.isSameA;
            isSameB = cpuResult.isSameB;
            distancesA = cpuResult.distancesA;
            distancesB = cpuResult.distancesB;
          }
        } else {
          // Worker disabled, CPU fallback (BVH ile)
          const cpuResult = await this.cpuFallback(pointsA, pointsB, tolerance, onProgress, bvhA, bvhB);
          isSameA = cpuResult.isSameA;
          isSameB = cpuResult.isSameB;
          distancesA = cpuResult.distancesA;
          distancesB = cpuResult.distancesB;
        }
      }

    } else {
      // ─────────────────────────────────────────────────────────────────────────
      // GEOMETRIC COMPARISON - BVH Surface veya Morton Code + Spatial Hash
      // ─────────────────────────────────────────────────────────────────────────
      onProgress?.(10, 'GPU başlatılıyor...');

      const gpuResult = await this.tryGPUCompare(pointsA, pointsB, tolerance, onProgress);

      if (gpuResult) {
        tier = 'gpu';
        isSameA = Array.from(gpuResult.isSameA).map(v => v === 1);
        isSameB = Array.from(gpuResult.isSameB).map(v => v === 1);
        distancesA = gpuResult.distancesA;
        distancesB = gpuResult.distancesB;
      } else if (this.options.useWorkerPool !== false) {
        // Worker Pool fallback
        const workerResult = await this.tryWorkerCompare(pointsA, pointsB, tolerance, onProgress);
        if (workerResult) {
          tier = 'worker';
          isSameA = workerResult.isSameA;
          isSameB = workerResult.isSameB;
          distancesA = workerResult.distancesA;
          distancesB = workerResult.distancesB;
        } else {
          // CPU fallback (BVH ile)
          const cpuResult = await this.cpuFallback(pointsA, pointsB, tolerance, onProgress, bvhA, bvhB);
          isSameA = cpuResult.isSameA;
          isSameB = cpuResult.isSameB;
          distancesA = cpuResult.distancesA;
          distancesB = cpuResult.distancesB;
        }
      } else {
        // Worker disabled, CPU fallback (BVH ile)
        const cpuResult = await this.cpuFallback(pointsA, pointsB, tolerance, onProgress, bvhA, bvhB);
        isSameA = cpuResult.isSameA;
        isSameB = cpuResult.isSameB;
        distancesA = cpuResult.distancesA;
        distancesB = cpuResult.distancesB;
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // BVH POST-REFINEMENT (GPU/Worker sonuçlarını düzelt)
    // GPU ve Worker vertex-to-vertex karşılaştırma yapar. Tessellation farkları
    // yüzünden aynı yüzeydeki vertex'ler "farklı" işaretlenebilir.
    // BVH ile gerçek yüzey mesafesini kontrol edip düzeltiriz.
    // ═══════════════════════════════════════════════════════════════════════════
    if (tier !== 'cpu' && bvhA && bvhB) {
      onProgress?.(85, 'BVH surface refinement yapılıyor...');
      await this.yieldToMain();

      const refinedA = await this.refineSameCheckWithBVH(isSameA, distancesA, pointsA, bvhB, tolerance);
      const refinedB = await this.refineSameCheckWithBVH(isSameB, distancesB, pointsB, bvhA, tolerance);

      if (refinedA > 0 || refinedB > 0) {
        console.log(`[FikoCadDiff] BVH surface refinement: A=${refinedA}, B=${refinedB} vertex düzeltildi`);
      }
    }

    onProgress?.(88, 'Mesafeler normalize ediliyor...');
    await this.yieldToMain();

    // GPU/Worker mesafelerini model diagonal'e göre clamp et (absürt değer önleme)
    const maxClampDist = this._modelDiagonal * 0.1;
    if (distancesA) {
      for (let i = 0; i < distancesA.length; i++) {
        if (distancesA[i] > maxClampDist) distancesA[i] = maxClampDist;
        if (!isFinite(distancesA[i])) distancesA[i] = maxClampDist;
      }
    }
    if (distancesB) {
      for (let i = 0; i < distancesB.length; i++) {
        if (distancesB[i] > maxClampDist) distancesB[i] = maxClampDist;
        if (!isFinite(distancesB[i])) distancesB[i] = maxClampDist;
      }
    }

    onProgress?.(90, 'İstatistikler hesaplanıyor...');
    await this.yieldToMain();

    // İstatistikler
    let farkliA = 0, farkliB = 0;
    for (let i = 0; i < isSameA.length; i++) {
      if (!isSameA[i]) farkliA++;
    }
    for (let i = 0; i < isSameB.length; i++) {
      if (!isSameB[i]) farkliB++;
    }

    const toleransIcinde = ((vertexCountA - farkliA + vertexCountB - farkliB) / (vertexCountA + vertexCountB)) * 100;

    // Sapma istatistikleri hesapla (distances varsa)
    let maxSapma = 0;
    let ortalamaSapma = 0;
    let stdSapma = 0;

    if (distancesA && distancesB && (distancesA.length > 0 || distancesB.length > 0)) {
      const stats = this.hesaplaSapmaIstatistikleri(distancesA, distancesB, isSameA, isSameB);
      maxSapma = stats.maxSapma;
      ortalamaSapma = stats.ortalamaSapma;
      stdSapma = stats.stdSapma;
    }

    onProgress?.(95, 'Görselleştirme oluşturuluyor...');
    await this.yieldToMain();

    // Geometry oluştur
    const showRemoved = this.options.comparisonType === 'added_removed' || this.options.comparisonType === 'removed_only';
    const showAdded = this.options.comparisonType === 'added_removed' || this.options.comparisonType === 'added_only';

    let meshA: THREE.BufferGeometry | null = null;
    let meshB: THREE.BufferGeometry | null = null;

    if (this.options.viewMode === 'both' || this.options.viewMode === 'old_only') {
      if (this.options.useHeatmapGradient && distancesA) {
        meshA = this.createGradientGeometry(meshlerA, distancesA, tolerance, showRemoved, true);
      } else if (this.options.refineBoundary !== false || this.options.showOnlyDifferences) {
        meshA = this.createRefinedCatiaGeometry(meshlerA, isSameA, pointsB, tolerance, showRemoved, true, centroidA, distancesA, bvhB);
      } else {
        meshA = this.createCatiaGeometry(meshlerA, isSameA, showRemoved, true);
      }
    }

    if (this.options.viewMode === 'both' || this.options.viewMode === 'new_only') {
      if (this.options.useHeatmapGradient && distancesB) {
        meshB = this.createGradientGeometry(meshlerB, distancesB, tolerance, showAdded, false);
      } else if (this.options.refineBoundary !== false || this.options.showOnlyDifferences) {
        meshB = this.createRefinedCatiaGeometry(meshlerB, isSameB, pointsA, tolerance, showAdded, false, centroidB, distancesB, bvhA);
      } else {
        meshB = this.createCatiaGeometry(meshlerB, isSameB, showAdded, false);
      }
    }

    // Edge geometry oluştur
    let edgesA: THREE.BufferGeometry | null = null;
    let edgesB: THREE.BufferGeometry | null = null;

    if (this.options.showEdges) {
      if (meshA) {
        edgesA = this.createEdgeGeometry(meshA);
      }
      if (meshB) {
        edgesB = this.createEdgeGeometry(meshB);
      }
    }

    // Orijinal geometry'leri oluştur (dialog için)
    const originalGeoA = this.createOriginalGeometry(meshlerA);
    const originalGeoB = this.createOriginalGeometry(meshlerB);

    onProgress?.(100, 'Tamamlandı');

    return {
      meshA,
      meshB,
      edgesA,
      edgesB,
      originalGeoA,
      originalGeoB,
      maxSapma,
      ortalamaSapma,
      stdSapma,
      toleransIcindeYuzde: toleransIcinde,
      sure: performance.now() - baslangic,
      vertexSayisiA: vertexCountA,
      vertexSayisiB: vertexCountB,
      renkSkalasi: { min: 0, max: maxSapma, tolerans: tolerance },
      tier,
      farkliVertexA: farkliA,
      farkliVertexB: farkliB,
      distancesA,
      distancesB
    };
  }

  /**
   * GPU ile karşılaştırma dene
   */
  private async tryGPUCompare(
    pointsA: Float32Array,
    pointsB: Float32Array,
    tolerance: number,
    onProgress?: ProgressCallback
  ): Promise<{ isSameA: Uint32Array; isSameB: Uint32Array; distancesA: Float32Array; distancesB: Float32Array } | null> {
    try {
      // GPU servisini başlat
      if (!this.gpuInitialized) {
        this.gpuService = getCadDiffGPU();
        this.gpuInitialized = await this.gpuService.init();
      }

      if (!this.gpuService?.isAvailable()) {
        return null;
      }

      onProgress?.(15, 'GPU karşılaştırma başlatılıyor...');

      const result = await this.gpuService.compare(
        pointsA,
        pointsB,
        tolerance,
        (p) => onProgress?.(15 + p * 0.7, `GPU karşılaştırma... %${Math.round(p)}`)
      );

      return result;
    } catch (err) {
      console.warn('[FikoCadDiff] GPU karşılaştırma hatası:', err);
      return null;
    }
  }

  /**
   * Visual GPU ile karşılaştırma dene (Jump Flood Algorithm)
   * O(log n) distance field - büyük modeller için ideal
   */
  private async tryVisualGPUCompare(
    pointsA: Float32Array,
    pointsB: Float32Array,
    tolerance: number,
    onProgress?: ProgressCallback
  ): Promise<{ isSameA: Uint32Array; isSameB: Uint32Array } | null> {
    try {
      // Visual GPU servisini başlat
      if (!this.visualGpuInitialized) {
        this.visualGpuService = getVisualDiffGPU();
        this.visualGpuInitialized = await this.visualGpuService.init();
      }

      if (!this.visualGpuService?.isAvailable()) {
        return null;
      }

      onProgress?.(15, 'Visual GPU (JFA) karşılaştırma başlatılıyor...');

      const result = await this.visualGpuService.compare(
        pointsA,
        pointsB,
        tolerance,
        (p, msg) => onProgress?.(15 + p * 0.7, msg || `Visual GPU... %${Math.round(p)}`)
      );

      // Get stats

      return result;
    } catch (err) {
      console.warn('[FikoCadDiff] Visual GPU karşılaştırma hatası:', err);
      return null;
    }
  }

  /**
   * Worker Pool ile karşılaştırma dene (multi-core paralel)
   * GPU başarısız olursa, CPU'dan önce worker pool denenir
   */
  private async tryWorkerCompare(
    pointsA: Float32Array,
    pointsB: Float32Array,
    tolerance: number,
    onProgress?: ProgressCallback
  ): Promise<{ isSameA: boolean[]; isSameB: boolean[]; distancesA: Float32Array; distancesB: Float32Array } | null> {
    try {
      if (!this.workerPoolInitialized) {
        this.workerPool = getCadDiffWorkerPool();
        this.workerPoolInitialized = await this.workerPool.initialize();
      }

      if (!this.workerPool?.isAvailable()) {
        return null;
      }

      onProgress?.(15, `Worker Pool (${this.workerPool.getWorkerCount()} core) başlatılıyor...`);

      // vec3 → vec4 format dönüşümü (worker pool vec4 kullanıyor)
      const vec4A = this.vec3ToVec4(pointsA);
      const vec4B = this.vec3ToVec4(pointsB);

      const cellSize = this.optimalCellSize(pointsA, pointsB);

      // A → B mesafe
      onProgress?.(20, 'Worker: Model A karşılaştırılıyor...');
      const resultA = await this.workerPool.computeDistances(vec4A, vec4B, cellSize);
      if (!resultA) return null;

      // B → A mesafe
      onProgress?.(55, 'Worker: Model B karşılaştırılıyor...');
      const resultB = await this.workerPool.computeDistances(vec4B, vec4A, cellSize);
      if (!resultB) return null;

      // Distance → isSame dönüşümü
      const countA = pointsA.length / 3;
      const countB = pointsB.length / 3;
      const isSameA: boolean[] = new Array(countA);
      const isSameB: boolean[] = new Array(countB);

      for (let i = 0; i < countA; i++) {
        isSameA[i] = resultA.distances[i] <= tolerance;
      }
      for (let i = 0; i < countB; i++) {
        isSameB[i] = resultB.distances[i] <= tolerance;
      }

      console.log(`[FikoCadDiff] Worker Pool tamamlandı: A=${resultA.cpuTimeMs.toFixed(0)}ms, B=${resultB.cpuTimeMs.toFixed(0)}ms`);

      return {
        isSameA,
        isSameB,
        distancesA: resultA.distances,
        distancesB: resultB.distances
      };
    } catch (err) {
      console.warn('[FikoCadDiff] Worker Pool hatası:', err);
      return null;
    }
  }

  /**
   * vec3 Float32Array → vec4 Float32Array dönüşümü (worker pool uyumluluğu)
   */
  private vec3ToVec4(points: Float32Array): Float32Array {
    const count = points.length / 3;
    const vec4 = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      vec4[i * 4] = points[i * 3];
      vec4[i * 4 + 1] = points[i * 3 + 1];
      vec4[i * 4 + 2] = points[i * 3 + 2];
      vec4[i * 4 + 3] = 1.0;
    }
    return vec4;
  }

  /**
   * CPU Fallback - GPU ve Worker başarısız olursa kullanılır
   *
   * BVH mevcutsa vertex-to-SURFACE karşılaştırma yapar (tessellation-invariant).
   * BVH yoksa eski SpatialHash3D vertex-to-vertex karşılaştırmaya düşer.
   */
  private async cpuFallback(
    pointsA: Float32Array,
    pointsB: Float32Array,
    tolerance: number,
    onProgress?: ProgressCallback,
    bvhA?: MeshBVH | null,
    bvhB?: MeshBVH | null
  ): Promise<{ isSameA: boolean[]; isSameB: boolean[]; distancesA: Float32Array; distancesB: Float32Array }> {
    onProgress?.(15, 'CPU karşılaştırma başlatılıyor...');

    this.cellSize = this.optimalCellSize(pointsA, pointsB);

    // BVH varsa vertex-to-surface karşılaştırma kullan (daha doğru)
    if (bvhB && bvhA) {
      onProgress?.(20, 'BVH surface karşılaştırma yapılıyor...');

      const resultA = await this.comparePointsBVHChunked(
        pointsA, bvhB, tolerance,
        (p) => onProgress?.(20 + p * 0.35, `Model A (BVH surface)... %${Math.round(p * 100)}`)
      );

      if (this.aborted) {
        return { isSameA: [], isSameB: [], distancesA: new Float32Array(0), distancesB: new Float32Array(0) };
      }

      const resultB = await this.comparePointsBVHChunked(
        pointsB, bvhA, tolerance,
        (p) => onProgress?.(55 + p * 0.35, `Model B (BVH surface)... %${Math.round(p * 100)}`)
      );

      console.log('[FikoCadDiff] CPU karşılaştırma: BVH vertex-to-surface (tessellation-invariant)');

      return {
        isSameA: resultA.isSame,
        isSameB: resultB.isSame,
        distancesA: resultA.distances,
        distancesB: resultB.distances
      };
    }

    // BVH yoksa eski SpatialHash3D fallback (vertex-to-vertex)
    console.warn('[FikoCadDiff] BVH oluşturulamadı, SpatialHash3D fallback kullanılıyor');
    onProgress?.(20, 'SpatialHash karşılaştırma yapılıyor...');

    const hashB = new SpatialHash3D(this.cellSize);
    hashB.build(pointsB);

    const hashA = new SpatialHash3D(this.cellSize);
    hashA.build(pointsA);

    const resultA = await this.comparePointsChunked(
      pointsA, hashB, tolerance,
      (p) => onProgress?.(20 + p * 0.35, `Model A karşılaştırılıyor... %${Math.round(p * 100)}`)
    );

    if (this.aborted) {
      return { isSameA: [], isSameB: [], distancesA: new Float32Array(0), distancesB: new Float32Array(0) };
    }

    const resultB = await this.comparePointsChunked(
      pointsB, hashA, tolerance,
      (p) => onProgress?.(55 + p * 0.35, `Model B karşılaştırılıyor... %${Math.round(p * 100)}`)
    );

    return {
      isSameA: resultA.isSame,
      isSameB: resultB.isSame,
      distancesA: resultA.distances,
      distancesB: resultB.distances
    };
  }

  /**
   * UI thread'e yield et - donmayı önle
   */
  private yieldToMain(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  /**
   * CHUNKED karşılaştırma - her chunk'tan sonra UI'a yield
   */
  private async comparePointsChunked(
    queryPoints: Float32Array,
    targetHash: SpatialHash3D,
    tolerance: number,
    onProgress?: (progress: number) => void
  ): Promise<{ isSame: boolean[]; distances: Float32Array }> {
    const count = queryPoints.length / 3;
    const isSame: boolean[] = new Array(count);
    const distances = new Float32Array(count);

    const CHUNK_SIZE = 5000; // Her chunk'ta 5000 vertex işle
    // searchDist: en yakın komşu araması için maks mesafe
    // Model diagonal'in %10'u ile sınırla - daha ötesi anlamsız
    const searchDist = Math.min(
      Math.max(tolerance * 50, this.cellSize * 5),
      this._modelDiagonal * 0.1
    );

    for (let start = 0; start < count; start += CHUNK_SIZE) {
      if (this.aborted) break;

      const end = Math.min(start + CHUNK_SIZE, count);

      for (let i = start; i < end; i++) {
        const idx = i * 3;
        const x = queryPoints[idx];
        const y = queryPoints[idx + 1];
        const z = queryPoints[idx + 2];

        const found = targetHash.hasPointWithin(x, y, z, tolerance);
        isSame[i] = found;

        if (!found) {
          distances[i] = targetHash.findNearest(x, y, z, searchDist);
        } else {
          distances[i] = 0;
        }
      }

      // Her chunk sonrası UI'a yield
      onProgress?.(end / count);
      await this.yieldToMain();
    }

    return { isSame, distances };
  }

  private extractWorldPoints(meshler: THREE.Mesh[]): Float32Array {
    let total = 0;
    for (const mesh of meshler) {
      const pos = mesh.geometry.getAttribute('position');
      if (pos) total += pos.count;
    }

    const points = new Float32Array(total * 3);
    let offset = 0;

    for (const mesh of meshler) {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position');
      if (!pos) continue;

      const matrix = mesh.matrixWorld;
      const e = matrix.elements;

      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);

        points[offset] = e[0] * x + e[4] * y + e[8] * z + e[12];
        points[offset + 1] = e[1] * x + e[5] * y + e[9] * z + e[13];
        points[offset + 2] = e[2] * x + e[6] * y + e[10] * z + e[14];

        offset += 3;
      }
    }

    return points;
  }

  private createCatiaGeometry(
    meshler: THREE.Mesh[],
    isSame: boolean[],
    showDifferences: boolean,
    isModelA: boolean = true  // true = model A (removed=kırmızı), false = model B (added=yeşil)
  ): THREE.BufferGeometry | null {
    if (meshler.length === 0) return null;

    const positions: number[] = [];
    const colors: number[] = [];
    const normals: number[] = [];

    let globalIdx = 0;

    for (const mesh of meshler) {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position');
      const norm = geo.getAttribute('normal');
      if (!pos) continue;

      const matrix = mesh.matrixWorld;
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
      const e = matrix.elements;
      const index = geo.getIndex();

      if (index) {
        for (let i = 0; i < index.count; i++) {
          const vertIdx = index.getX(i);

          const x = pos.getX(vertIdx);
          const y = pos.getY(vertIdx);
          const z = pos.getZ(vertIdx);

          positions.push(
            e[0] * x + e[4] * y + e[8] * z + e[12],
            e[1] * x + e[5] * y + e[9] * z + e[13],
            e[2] * x + e[6] * y + e[10] * z + e[14]
          );

          if (norm) {
            const nx = norm.getX(vertIdx);
            const ny = norm.getY(vertIdx);
            const nz = norm.getZ(vertIdx);
            const ne = normalMatrix.elements;
            const tnx = ne[0] * nx + ne[3] * ny + ne[6] * nz;
            const tny = ne[1] * nx + ne[4] * ny + ne[7] * nz;
            const tnz = ne[2] * nx + ne[5] * ny + ne[8] * nz;
            const len = Math.sqrt(tnx * tnx + tny * tny + tnz * tnz) || 1;
            normals.push(tnx / len, tny / len, tnz / len);
          }

          const same = isSame[globalIdx + vertIdx] ?? true;
          if (same) {
            colors.push(this.COLOR_SAME.r, this.COLOR_SAME.g, this.COLOR_SAME.b);
          } else if (showDifferences) {
            // Model A = Removed (kırmızı), Model B = Added (yeşil)
            const diffColor = isModelA ? this.COLOR_REMOVED : this.COLOR_ADDED;
            colors.push(diffColor.r, diffColor.g, diffColor.b);
          } else {
            colors.push(this.COLOR_SAME.r, this.COLOR_SAME.g, this.COLOR_SAME.b);
          }
        }

        globalIdx += pos.count;
      } else {
        for (let i = 0; i < pos.count; i++) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);

          positions.push(
            e[0] * x + e[4] * y + e[8] * z + e[12],
            e[1] * x + e[5] * y + e[9] * z + e[13],
            e[2] * x + e[6] * y + e[10] * z + e[14]
          );

          if (norm) {
            const nx = norm.getX(i);
            const ny = norm.getY(i);
            const nz = norm.getZ(i);
            const ne = normalMatrix.elements;
            const tnx = ne[0] * nx + ne[3] * ny + ne[6] * nz;
            const tny = ne[1] * nx + ne[4] * ny + ne[7] * nz;
            const tnz = ne[2] * nx + ne[5] * ny + ne[8] * nz;
            const len = Math.sqrt(tnx * tnx + tny * tny + tnz * tnz) || 1;
            normals.push(tnx / len, tny / len, tnz / len);
          }

          const same = isSame[globalIdx + i] ?? true;
          if (same) {
            colors.push(this.COLOR_SAME.r, this.COLOR_SAME.g, this.COLOR_SAME.b);
          } else if (showDifferences) {
            // Model A = Removed (kırmızı), Model B = Added (yeşil)
            const diffColor = isModelA ? this.COLOR_REMOVED : this.COLOR_ADDED;
            colors.push(diffColor.r, diffColor.g, diffColor.b);
          } else {
            colors.push(this.COLOR_SAME.r, this.COLOR_SAME.g, this.COLOR_SAME.b);
          }
        }

        globalIdx += pos.count;
      }
    }

    if (positions.length === 0) return null;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    if (normals.length > 0) {
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    } else {
      geometry.computeVertexNormals();
    }

    return geometry;
  }

  /**
   * Sınır üçgenlerini subdivide ederek daha hassas geometry oluşturur.
   *
   * Mantık:
   * 1. Her üçgenin 3 vertex'inin isSame durumuna bak
   * 2. Hepsi aynıysa (hep true veya hep false) → doğrudan emit (eski davranış)
   * 3. Karışıksa (sınır üçgeni) → midpoint subdivision ile 4 alt üçgene böl
   * 4. Yeni midpoint vertex'leri target model'e karşı kontrol et
   *
   * Sonuç: Sınır bölgelerinde 4x daha fazla geometri, daha hassas boyama
   * Eski createCatiaGeometry'ye DOKUNMAZ.
   */
  private createRefinedCatiaGeometry(
    meshler: THREE.Mesh[],
    isSame: boolean[],
    targetPoints: Float32Array,
    tolerance: number,
    showDifferences: boolean,
    isModelA: boolean,
    centroid?: { x: number; y: number; z: number },
    distances?: Float32Array,
    targetBVH?: MeshBVH | null
  ): THREE.BufferGeometry | null {
    if (meshler.length === 0) return null;

    // Spatial hash for boundary midpoint checks (BVH fallback)
    const cellSize = Math.max(tolerance * 2, 0.1);
    const targetHash = new SpatialHash3D(cellSize);
    targetHash.build(targetPoints);

    const positions: number[] = [];
    const colors: number[] = [];
    const normals: number[] = [];

    const cSame = this.COLOR_SAME;
    const cDiff = isModelA ? this.COLOR_REMOVED : this.COLOR_ADDED;

    // Centroid offset for useLocalAxis (0 if not used)
    const ox = centroid?.x ?? 0;
    const oy = centroid?.y ?? 0;
    const oz = centroid?.z ?? 0;

    const onlyDiffs = this.options.showOnlyDifferences === true;
    const doSubdivide = this.options.refineBoundary !== false;
    const maxDepth = this.options.subdivisionDepth ?? (onlyDiffs ? 3 : 1);

    // ─── Distance-based filtering ───
    // STEP tessellator aynı geometriyi farklı vertex pozisyonlarıyla üretir.
    // isSame=false ama mesafe çok küçükse → tessellation artifact'ı, gerçek değişiklik değil.
    // surfaceThreshold: Bu mesafenin altındaki vertex'ler "görsel olarak aynı" kabul edilir.
    const hasDistances = distances && distances.length > 0 && onlyDiffs;
    const filterMul = this.options.surfaceFilterMultiplier ?? 10;
    const surfaceThreshold = tolerance * filterMul;

    // "Display-same" = isSame VEYA mesafesi surfaceThreshold altında
    const isDisplaySame = (idx: number): boolean => {
      if (isSame[idx]) return true;
      if (hasDistances && distances![idx] !== undefined && distances![idx] <= surfaceThreshold) return true;
      return false;
    };

    let globalIdx = 0;
    let refinedCount = 0;
    let skippedCount = 0;
    let distanceFilteredCount = 0;

    // ─── Method-scope helpers (mesh-bağımsız) ───

    const pushV = (px: number, py: number, pz: number,
                   nx: number, ny: number, nz: number, same: boolean) => {
      positions.push(px, py, pz);
      normals.push(nx, ny, nz);
      const c = (same || !showDifferences) ? cSame : cDiff;
      colors.push(c.r, c.g, c.b);
    };

    const avgN = (a: number[], b: number[]): number[] => {
      const x = a[0]+b[0], y = a[1]+b[1], z = a[2]+b[2];
      const len = Math.sqrt(x*x + y*y + z*z) || 1;
      return [x/len, y/len, z/len];
    };

    // Midpoint mesafe kontrolü: BVH varsa vertex-to-surface, yoksa vertex-to-vertex
    const _bvhQueryPt = new THREE.Vector3();
    const _bvhTarget = { point: new THREE.Vector3(), distance: 0, faceIndex: 0 };

    const chkPtDisplaySame = (p: number[]): boolean => {
      const qx = p[0] - ox, qy = p[1] - oy, qz = p[2] - oz;

      // BVH varsa vertex-to-surface mesafe kullan (tessellation-invariant)
      if (targetBVH) {
        _bvhQueryPt.set(qx, qy, qz);
        const result = targetBVH.closestPointToPoint(_bvhQueryPt, _bvhTarget, 0, surfaceThreshold);
        if (result && result.distance <= tolerance) return true;
        if (result && result.distance <= surfaceThreshold) return true;
        return false;
      }

      // Fallback: SpatialHash vertex-to-vertex
      if (targetHash.hasPointWithin(qx, qy, qz, tolerance)) return true;
      if (hasDistances) {
        const dist = targetHash.findNearest(qx, qy, qz, surfaceThreshold);
        if (dist <= surfaceThreshold) return true;
      }
      return false;
    };

    /**
     * Rekürsif üçgen subdivision.
     * - Tüm vertex'ler aynı → emit veya skip (onlyDiffs)
     * - Karışık (sınır) ve depth < maxDepth → 4 alt üçgene böl, recurse
     * - maxDepth'e ulaşıldı → olduğu gibi emit
     */
    const subdivideTri = (
      p0: number[], n0: number[], s0: boolean,
      p1: number[], n1: number[], s1: boolean,
      p2: number[], n2: number[], s2: boolean,
      depth: number
    ) => {
      // Tüm vertex'ler aynı durumda
      if (s0 === s1 && s1 === s2) {
        if (s0 && onlyDiffs) { skippedCount++; return; }
        pushV(p0[0],p0[1],p0[2], n0[0],n0[1],n0[2], s0);
        pushV(p1[0],p1[1],p1[2], n1[0],n1[1],n1[2], s1);
        pushV(p2[0],p2[1],p2[2], n2[0],n2[1],n2[2], s2);
        return;
      }

      // Max derinliğe ulaşıldı
      if (depth >= maxDepth) {
        if (onlyDiffs) {
          // Sadece farklar modunda: karışık sınır üçgenlerini atla.
          // Sadece tüm vertex'leri diff olan üçgenleri göster → temiz kesim.
          const diffCount = (!s0 ? 1 : 0) + (!s1 ? 1 : 0) + (!s2 ? 1 : 0);
          if (diffCount < 2) { skippedCount++; return; } // çoğunluk same → atla
          // Çoğunluk diff → tüm vertex'leri diff rengiyle emit (temiz görünüm)
          pushV(p0[0],p0[1],p0[2], n0[0],n0[1],n0[2], false);
          pushV(p1[0],p1[1],p1[2], n1[0],n1[1],n1[2], false);
          pushV(p2[0],p2[1],p2[2], n2[0],n2[1],n2[2], false);
        } else {
          pushV(p0[0],p0[1],p0[2], n0[0],n0[1],n0[2], s0);
          pushV(p1[0],p1[1],p1[2], n1[0],n1[1],n1[2], s1);
          pushV(p2[0],p2[1],p2[2], n2[0],n2[1],n2[2], s2);
        }
        return;
      }

      refinedCount++;

      // Kenar orta noktaları
      const m01 = [(p0[0]+p1[0])/2, (p0[1]+p1[1])/2, (p0[2]+p1[2])/2];
      const m12 = [(p1[0]+p2[0])/2, (p1[1]+p2[1])/2, (p1[2]+p2[2])/2];
      const m20 = [(p2[0]+p0[0])/2, (p2[1]+p0[1])/2, (p2[2]+p0[2])/2];

      // Orta nokta normalleri
      const nm01 = avgN(n0, n1);
      const nm12 = avgN(n1, n2);
      const nm20 = avgN(n2, n0);

      // Orta noktaları target model'e karşı kontrol (distance-aware)
      const sm01 = chkPtDisplaySame(m01);
      const sm12 = chkPtDisplaySame(m12);
      const sm20 = chkPtDisplaySame(m20);

      // 4 alt üçgene rekürsif dallan
      const d = depth + 1;
      subdivideTri(p0, n0, s0,   m01, nm01, sm01,  m20, nm20, sm20, d);
      subdivideTri(m01, nm01, sm01,  p1, n1, s1,   m12, nm12, sm12, d);
      subdivideTri(m20, nm20, sm20,  m12, nm12, sm12,  p2, n2, s2,  d);
      subdivideTri(m01, nm01, sm01,  m12, nm12, sm12,  m20, nm20, sm20, d);
    };

    // ─── Mesh loop ───

    for (const mesh of meshler) {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position');
      const norm = geo.getAttribute('normal');
      if (!pos) continue;

      const matrix = mesh.matrixWorld;
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
      const me = matrix.elements;
      const ne = normalMatrix.elements;
      const index = geo.getIndex();

      // Pre-allocated buffers: 3 vertices × 3 components
      const wp = new Float32Array(9);  // world positions
      const wn = new Float32Array(9);  // world normals

      const transformVertex = (vi: number, out: Float32Array, off: number) => {
        const x = pos.getX(vi), y = pos.getY(vi), z = pos.getZ(vi);
        out[off]   = me[0]*x + me[4]*y + me[8]*z + me[12];
        out[off+1] = me[1]*x + me[5]*y + me[9]*z + me[13];
        out[off+2] = me[2]*x + me[6]*y + me[10]*z + me[14];
      };

      const transformNormal = (vi: number, out: Float32Array, off: number) => {
        if (!norm) { out[off] = 0; out[off+1] = 1; out[off+2] = 0; return; }
        const nx = norm.getX(vi), ny = norm.getY(vi), nz = norm.getZ(vi);
        const tnx = ne[0]*nx + ne[3]*ny + ne[6]*nz;
        const tny = ne[1]*nx + ne[4]*ny + ne[7]*nz;
        const tnz = ne[2]*nx + ne[5]*ny + ne[8]*nz;
        const len = Math.sqrt(tnx*tnx + tny*tny + tnz*tnz) || 1;
        out[off] = tnx/len; out[off+1] = tny/len; out[off+2] = tnz/len;
      };

      const getIdx = index
        ? (offset: number) => index.getX(offset)
        : (offset: number) => offset;

      const triCount = index ? Math.floor(index.count / 3) : Math.floor(pos.count / 3);

      for (let t = 0; t < triCount; t++) {
        const vi0 = getIdx(t * 3);
        const vi1 = getIdx(t * 3 + 1);
        const vi2 = getIdx(t * 3 + 2);

        transformVertex(vi0, wp, 0);
        transformVertex(vi1, wp, 3);
        transformVertex(vi2, wp, 6);
        transformNormal(vi0, wn, 0);
        transformNormal(vi1, wn, 3);
        transformNormal(vi2, wn, 6);

        // Distance-based display-same kontrolü (tessellation artifact filtreleme)
        const s0 = isDisplaySame(globalIdx + vi0);
        const s1 = isDisplaySame(globalIdx + vi1);
        const s2 = isDisplaySame(globalIdx + vi2);

        // onlyDiffs modunda ek kontrol: üçgen centroid'ini target'a karşı kontrol et
        // Bu, vertex-bazlı kontrolü tamamlar ve sınır sızıntılarını önler
        if (onlyDiffs && (s0 || s1 || s2)) {
          // Centroid hesapla
          const cx = (wp[0] + wp[3] + wp[6]) / 3;
          const cy = (wp[1] + wp[4] + wp[7]) / 3;
          const cz = (wp[2] + wp[5] + wp[8]) / 3;
          const centroidSame = chkPtDisplaySame([cx, cy, cz]);

          if (s0 && s1 && s2) {
            // Tüm vertex'ler same → kesinlikle atla
            if (hasDistances && !isSame[globalIdx + vi0]) distanceFilteredCount++;
            else skippedCount++;
            continue;
          }

          // Karışık üçgen ama centroid "same" → büyük ihtimalle aynı yüzeyin sınırı
          if (centroidSame) {
            const diffCount = (!s0 ? 1 : 0) + (!s1 ? 1 : 0) + (!s2 ? 1 : 0);
            if (diffCount <= 1) {
              // 1/3 diff, centroid same → sınır sızıntısı, atla
              skippedCount++;
              continue;
            }
          }
        }

        if (s0 === s1 && s1 === s2) {
          // Tüm vertex'ler aynı durumda → hızlı yol (allokasyon yok)
          // (onlyDiffs'te all-same zaten yukarıda atlandı)
          pushV(wp[0],wp[1],wp[2], wn[0],wn[1],wn[2], s0);
          pushV(wp[3],wp[4],wp[5], wn[3],wn[4],wn[5], s1);
          pushV(wp[6],wp[7],wp[8], wn[6],wn[7],wn[8], s2);
        } else if (doSubdivide) {
          // SINIR ÜÇGENİ → rekürsif subdivision
          subdivideTri(
            [wp[0],wp[1],wp[2]], [wn[0],wn[1],wn[2]], s0,
            [wp[3],wp[4],wp[5]], [wn[3],wn[4],wn[5]], s1,
            [wp[6],wp[7],wp[8]], [wn[6],wn[7],wn[8]], s2,
            0
          );
        } else if (onlyDiffs) {
          // Subdivision kapalı ama onlyDiffs aktif:
          // Karışık üçgeni tamamen diff olarak emit et (temiz sınır)
          pushV(wp[0],wp[1],wp[2], wn[0],wn[1],wn[2], false);
          pushV(wp[3],wp[4],wp[5], wn[3],wn[4],wn[5], false);
          pushV(wp[6],wp[7],wp[8], wn[6],wn[7],wn[8], false);
        } else {
          // Normal mod, subdivision kapalı → per-vertex renk
          pushV(wp[0],wp[1],wp[2], wn[0],wn[1],wn[2], s0);
          pushV(wp[3],wp[4],wp[5], wn[3],wn[4],wn[5], s1);
          pushV(wp[6],wp[7],wp[8], wn[6],wn[7],wn[8], s2);
        }
      }

      globalIdx += pos.count;
    }

    if (refinedCount > 0 || skippedCount > 0 || distanceFilteredCount > 0) {
      console.log(`[FikoCadDiff] Rekürsif refinement (depth=${maxDepth}): ${refinedCount} subdivision, ${skippedCount} aynı üçgen atlandı, ${distanceFilteredCount} tessellation artifact filtrelendi`);
    }

    if (positions.length === 0) return null;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    if (normals.length > 0) {
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    } else {
      geometry.computeVertexNormals();
    }

    return geometry;
  }

  /**
   * Mesafe bazlı gradient heatmap geometry oluştur
   * Eski createCatiaGeometry'ye DOKUNMAZ - yanına yeni metod olarak eklenir
   *
   * Renk skalası:
   * - distance = 0 → COLOR_SAME (sarı)
   * - 0 < distance <= tolerance → sarı → turuncu (yakın fark)
   * - distance > tolerance → turuncu → kırmızı(A)/yeşil(B) (büyük fark)
   */
  private createGradientGeometry(
    meshler: THREE.Mesh[],
    distances: Float32Array,
    tolerance: number,
    showDifferences: boolean,
    isModelA: boolean
  ): THREE.BufferGeometry | null {
    if (meshler.length === 0) return null;

    const positions: number[] = [];
    const colors: number[] = [];
    const normals: number[] = [];

    // Ara renk: turuncu (sarı→kırmızı/yeşil geçiş noktası)
    const COLOR_WARN = { r: 1.0, g: 0.55, b: 0.0 }; // turuncu

    const diffColor = isModelA ? this.COLOR_REMOVED : this.COLOR_ADDED;
    const maxGradientDist = tolerance * 5; // Bu mesafeden sonra tam fark rengi

    let globalIdx = 0;

    for (const mesh of meshler) {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position');
      const norm = geo.getAttribute('normal');
      if (!pos) continue;

      const matrix = mesh.matrixWorld;
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
      const e = matrix.elements;
      const index = geo.getIndex();

      const processVertex = (vertIdx: number, distIdx: number) => {
        const x = pos.getX(vertIdx);
        const y = pos.getY(vertIdx);
        const z = pos.getZ(vertIdx);

        positions.push(
          e[0] * x + e[4] * y + e[8] * z + e[12],
          e[1] * x + e[5] * y + e[9] * z + e[13],
          e[2] * x + e[6] * y + e[10] * z + e[14]
        );

        if (norm) {
          const nx = norm.getX(vertIdx);
          const ny = norm.getY(vertIdx);
          const nz = norm.getZ(vertIdx);
          const ne = normalMatrix.elements;
          const tnx = ne[0] * nx + ne[3] * ny + ne[6] * nz;
          const tny = ne[1] * nx + ne[4] * ny + ne[7] * nz;
          const tnz = ne[2] * nx + ne[5] * ny + ne[8] * nz;
          const len = Math.sqrt(tnx * tnx + tny * tny + tnz * tnz) || 1;
          normals.push(tnx / len, tny / len, tnz / len);
        }

        const dist = distances[distIdx] ?? 0;

        if (dist <= 0) {
          // Aynı → sarı
          colors.push(this.COLOR_SAME.r, this.COLOR_SAME.g, this.COLOR_SAME.b);
        } else if (!showDifferences) {
          colors.push(this.COLOR_SAME.r, this.COLOR_SAME.g, this.COLOR_SAME.b);
        } else if (dist <= tolerance) {
          // Tolerans içi → sarı → turuncu
          const t = dist / tolerance;
          colors.push(
            this.COLOR_SAME.r + (COLOR_WARN.r - this.COLOR_SAME.r) * t,
            this.COLOR_SAME.g + (COLOR_WARN.g - this.COLOR_SAME.g) * t,
            this.COLOR_SAME.b + (COLOR_WARN.b - this.COLOR_SAME.b) * t
          );
        } else {
          // Tolerans dışı → turuncu → kırmızı/yeşil
          const t = Math.min((dist - tolerance) / (maxGradientDist - tolerance), 1.0);
          colors.push(
            COLOR_WARN.r + (diffColor.r - COLOR_WARN.r) * t,
            COLOR_WARN.g + (diffColor.g - COLOR_WARN.g) * t,
            COLOR_WARN.b + (diffColor.b - COLOR_WARN.b) * t
          );
        }
      };

      if (index) {
        for (let i = 0; i < index.count; i++) {
          const vertIdx = index.getX(i);
          processVertex(vertIdx, globalIdx + vertIdx);
        }
        globalIdx += pos.count;
      } else {
        for (let i = 0; i < pos.count; i++) {
          processVertex(i, globalIdx + i);
        }
        globalIdx += pos.count;
      }
    }

    if (positions.length === 0) return null;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    if (normals.length > 0) {
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    } else {
      geometry.computeVertexNormals();
    }

    return geometry;
  }

  /**
   * Sapma istatistiklerini hesapla (farklı vertex'lerin mesafeleri üzerinden)
   * - Distances model diagonal'in %10'u ile clamp edilir (outlier temizliği)
   * - maxSapma olarak P99 (99. yüzdelik) kullanılır (gerçek max yerine)
   */
  private hesaplaSapmaIstatistikleri(
    distancesA: Float32Array,
    distancesB: Float32Array,
    isSameA: boolean[],
    isSameB: boolean[]
  ): { maxSapma: number; ortalamaSapma: number; stdSapma: number } {
    // Mesafe üst sınırı: model diagonal'in %10'u (bunun ötesi outlier)
    const maxReasonableDistance = this._modelDiagonal * 0.1;

    // Farklı vertex mesafelerini topla (clamped)
    const diffs: number[] = [];
    for (let i = 0; i < distancesA.length; i++) {
      if (!isSameA[i] && distancesA[i] > 0) {
        diffs.push(Math.min(distancesA[i], maxReasonableDistance));
      }
    }
    for (let i = 0; i < distancesB.length; i++) {
      if (!isSameB[i] && distancesB[i] > 0) {
        diffs.push(Math.min(distancesB[i], maxReasonableDistance));
      }
    }

    if (diffs.length === 0) {
      return { maxSapma: 0, ortalamaSapma: 0, stdSapma: 0 };
    }

    // Sırala → percentile hesapla
    diffs.sort((a, b) => a - b);

    // P99 (99. yüzdelik) - outlier'ları dışlar
    const p99Idx = Math.min(Math.floor(diffs.length * 0.99), diffs.length - 1);
    const maxSapma = diffs[p99Idx];

    // Ortalama (P99'a kadar olan mesafeler üzerinden)
    let toplamSapma = 0;
    const countForAvg = p99Idx + 1;
    for (let i = 0; i <= p99Idx; i++) {
      toplamSapma += diffs[i];
    }
    const ortalamaSapma = toplamSapma / countForAvg;

    // Standart sapma
    let toplamKareFark = 0;
    for (let i = 0; i <= p99Idx; i++) {
      const fark = diffs[i] - ortalamaSapma;
      toplamKareFark += fark * fark;
    }
    const stdSapma = Math.sqrt(toplamKareFark / countForAvg);

    return { maxSapma, ortalamaSapma, stdSapma };
  }

  /**
   * Nokta bulutunu centroid'e normalize et (yerel eksen sistemi)
   * Her iki modeli kendi merkezine taşır - böylece farklı pozisyonlardaki
   * aynı geometri doğru eşleşir.
   *
   * @param points vec3 Float32Array (in-place değiştirir)
   * @returns centroid { x, y, z }
   */
  private normalizeToCenter(points: Float32Array): { x: number; y: number; z: number } {
    const count = points.length / 3;
    if (count === 0) return { x: 0, y: 0, z: 0 };

    // Centroid hesapla
    let cx = 0, cy = 0, cz = 0;
    for (let i = 0; i < points.length; i += 3) {
      cx += points[i];
      cy += points[i + 1];
      cz += points[i + 2];
    }
    cx /= count;
    cy /= count;
    cz /= count;

    // Centroid'i çıkar (merkeze taşı)
    for (let i = 0; i < points.length; i += 3) {
      points[i] -= cx;
      points[i + 1] -= cy;
      points[i + 2] -= cz;
    }

    return { x: cx, y: cy, z: cz };
  }

  private optimalCellSize(pointsA: Float32Array, pointsB: Float32Array): number {
    const boundsA = this.calculateBounds(pointsA);
    const boundsB = this.calculateBounds(pointsB);

    const dx = Math.max(boundsA.max.x - boundsA.min.x, boundsB.max.x - boundsB.min.x);
    const dy = Math.max(boundsA.max.y - boundsA.min.y, boundsB.max.y - boundsB.min.y);
    const dz = Math.max(boundsA.max.z - boundsA.min.z, boundsB.max.z - boundsB.min.z);
    const diagonal = Math.sqrt(dx * dx + dy * dy + dz * dz);

    return Math.max(this.options.computationAccuracy * 2, diagonal / 100, 0.1);
  }

  private calculateBounds(points: Float32Array): {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  } {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (let i = 0; i < points.length; i += 3) {
      const x = points[i], y = points[i + 1], z = points[i + 2];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      if (z > maxZ) maxZ = z;
    }

    return {
      min: { x: minX, y: minY, z: minZ },
      max: { x: maxX, y: maxY, z: maxZ }
    };
  }

  private meshTopla(nesne: THREE.Object3D): THREE.Mesh[] {
    const meshler: THREE.Mesh[] = [];

    nesne.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        const pos = child.geometry.getAttribute('position');
        if (pos && pos.count > 0 && !this.isExcluded(child)) {
          meshler.push(child);
        }
      }
    });

    return meshler;
  }

  private isExcluded(mesh: THREE.Mesh): boolean {
    const name = (mesh.name || '').toLowerCase();
    for (const ex of this.EXCLUDED_NAMES) {
      if (name.includes(ex)) return true;
    }

    let parent = mesh.parent;
    while (parent) {
      const pName = (parent.name || '').toLowerCase();
      for (const ex of this.EXCLUDED_NAMES) {
        if (pName.includes(ex)) return true;
      }
      parent = parent.parent;
    }

    return false;
  }

  /**
   * Edge geometry oluştur - THREE.EdgesGeometry kullanarak
   */
  private createEdgeGeometry(meshGeometry: THREE.BufferGeometry): THREE.BufferGeometry {
    // EdgesGeometry açı eşiği (derece) - keskin kenarları tespit eder
    const thresholdAngle = 30;
    const edgesGeo = new THREE.EdgesGeometry(meshGeometry, thresholdAngle);
    return edgesGeo;
  }

  /**
   * Orijinal (birleşik) geometry oluştur - dialog için
   * Mesh array'ini alıp tek bir geometry döndürür
   */
  private createOriginalGeometry(meshler: THREE.Mesh[]): THREE.BufferGeometry | null {
    if (!meshler || meshler.length === 0) return null;

    const geometries: THREE.BufferGeometry[] = [];

    for (const mesh of meshler) {
      if (!mesh.geometry) continue;

      try {
        let geo = mesh.geometry.clone();

        // Position attribute kontrolü
        if (!geo.attributes.position) continue;

        // Index'li geometry'yi non-indexed'a çevir (mergeGeometries uyumluluğu için)
        if (geo.index) {
          geo = geo.toNonIndexed();
        }

        // Sadece position attribute'u tut (diğerleri uyumsuzluk yaratabilir)
        const posAttr = geo.attributes.position;
        geo = new THREE.BufferGeometry();
        geo.setAttribute('position', posAttr);

        // World matrix uygula
        mesh.updateWorldMatrix(true, false);
        geo.applyMatrix4(mesh.matrixWorld);

        // Normal hesapla
        geo.computeVertexNormals();

        geometries.push(geo);
      } catch (e) {
        // Clone başarısız olursa atla
      }
    }

    if (geometries.length === 0) return null;
    if (geometries.length === 1) return geometries[0];

    // Birleştir
    try {
      return mergeGeometries(geometries, false);
    } catch (e) {
      // Hala başarısız olursa ilk geometry'yi döndür
      console.warn('[FikoCadDiff] Original geometry merge failed, using first geometry');
      return geometries[0];
    }
  }

  /**
   * Mesh dizisinden BVH oluştur (vertex-to-surface karşılaştırma için)
   *
   * Neden BVH?
   * - SpatialHash3D: vertex-to-vertex mesafe → tessellation farklılıklarında yanlış "farklı" sonuç
   * - MeshBVH: vertex-to-SURFACE mesafe → aynı yüzey farklı tessellate edilse bile doğru sonuç
   *
   * @param meshler Kaynak mesh dizisi
   * @param centroid Centroid offset (useLocalAxis aktifse)
   * @returns MeshBVH veya null (hata durumunda)
   */
  private buildBVHFromMeshes(
    meshler: THREE.Mesh[],
    centroid?: { x: number; y: number; z: number }
  ): MeshBVH | null {
    if (!meshler || meshler.length === 0) return null;

    const geometries: THREE.BufferGeometry[] = [];

    for (const mesh of meshler) {
      if (!mesh.geometry) continue;

      try {
        let geo = mesh.geometry.clone();
        if (!geo.attributes.position) continue;

        if (geo.index) {
          geo = geo.toNonIndexed();
        }

        // Sadece position attribute tut (BVH için yeterli)
        const posAttr = geo.attributes.position;
        geo = new THREE.BufferGeometry();
        geo.setAttribute('position', posAttr);

        // World matrix uygula
        mesh.updateWorldMatrix(true, false);
        geo.applyMatrix4(mesh.matrixWorld);

        geometries.push(geo);
      } catch (e) {
        // Clone başarısız → atla
      }
    }

    if (geometries.length === 0) return null;

    let merged: THREE.BufferGeometry | null;
    if (geometries.length === 1) {
      merged = geometries[0];
    } else {
      try {
        merged = mergeGeometries(geometries, false);
      } catch (e) {
        merged = geometries[0];
      }
    }

    if (!merged) return null;

    // Centroid offset uygula (useLocalAxis aktifse)
    if (centroid) {
      const pos = merged.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        pos.setXYZ(i,
          pos.getX(i) - centroid.x,
          pos.getY(i) - centroid.y,
          pos.getZ(i) - centroid.z
        );
      }
      pos.needsUpdate = true;
    }

    try {
      return new MeshBVH(merged, { maxLeafTris: 10, strategy: SAH });
    } catch (e) {
      console.warn('[FikoCadDiff] BVH oluşturulamadı:', e);
      return null;
    }
  }

  /**
   * BVH tabanlı vertex-to-surface karşılaştırma (chunked, non-blocking)
   *
   * Her vertex için target model'in YÜZEY'ine (üçgenlerine) olan mesafeyi hesaplar.
   * SpatialHash3D'den farklı olarak, aynı yüzeyin farklı tessellasyonunu doğru tanır.
   */
  private async comparePointsBVHChunked(
    queryPoints: Float32Array,
    targetBVH: MeshBVH,
    tolerance: number,
    onProgress?: (progress: number) => void
  ): Promise<{ isSame: boolean[]; distances: Float32Array }> {
    const count = queryPoints.length / 3;
    const isSame: boolean[] = new Array(count);
    const distances = new Float32Array(count);

    const CHUNK_SIZE = 5000;
    const searchDist = Math.min(
      Math.max(tolerance * 50, this.cellSize * 5),
      this._modelDiagonal * 0.1
    );

    // Pre-allocated query objects (zero allocation in loop)
    const queryPoint = new THREE.Vector3();
    const target = { point: new THREE.Vector3(), distance: 0, faceIndex: 0 };

    for (let start = 0; start < count; start += CHUNK_SIZE) {
      if (this.aborted) break;

      const end = Math.min(start + CHUNK_SIZE, count);

      for (let i = start; i < end; i++) {
        const idx = i * 3;
        queryPoint.set(queryPoints[idx], queryPoints[idx + 1], queryPoints[idx + 2]);

        const result = targetBVH.closestPointToPoint(queryPoint, target, 0, searchDist);

        if (result) {
          isSame[i] = result.distance <= tolerance;
          distances[i] = result.distance;
        } else {
          isSame[i] = false;
          distances[i] = searchDist;
        }
      }

      onProgress?.(end / count);
      await this.yieldToMain();
    }

    return { isSame, distances };
  }

  /**
   * GPU/Worker sonuçlarını BVH ile yeniden kontrol et (post-refinement)
   *
   * GPU ve Worker vertex-to-vertex karşılaştırma yapar. Bu yüzden tessellation
   * farklılıklarında "farklı" olarak işaretledikleri vertex'ler aslında aynı
   * yüzeyin üzerinde olabilir. Bu metod borderline vertex'leri BVH ile
   * yeniden kontrol eder ve gerekirse "aynı" olarak düzeltir.
   */
  private async refineSameCheckWithBVH(
    isSame: boolean[],
    distances: Float32Array | undefined,
    queryPoints: Float32Array,
    targetBVH: MeshBVH,
    tolerance: number
  ): Promise<number> {
    if (!distances || !targetBVH) return 0;

    const surfaceThreshold = tolerance * (this.options.surfaceFilterMultiplier ?? 10);
    const queryPoint = new THREE.Vector3();
    const target = { point: new THREE.Vector3(), distance: 0, faceIndex: 0 };

    let refined = 0;
    for (let i = 0; i < isSame.length; i++) {
      if (!isSame[i] && distances[i] > 0 && distances[i] <= surfaceThreshold) {
        // Bu vertex GPU/Worker tarafından "farklı" işaretlenmiş ama mesafesi küçük.
        // BVH ile gerçek yüzey mesafesini kontrol et.
        const idx = i * 3;
        queryPoint.set(queryPoints[idx], queryPoints[idx + 1], queryPoints[idx + 2]);

        const result = targetBVH.closestPointToPoint(queryPoint, target, 0, surfaceThreshold);
        if (result && result.distance <= tolerance) {
          isSame[i] = true;
          distances[i] = result.distance;
          refined++;
        } else if (result) {
          // Mesafeyi güncelle (vertex-to-surface mesafe daha doğru)
          distances[i] = result.distance;
        }
      }
    }

    return refined;
  }

  private bosSonuc(sure: number): CadDiffSonuc {
    return {
      meshA: null,
      meshB: null,
      edgesA: null,
      edgesB: null,
      originalGeoA: null,
      originalGeoB: null,
      maxSapma: 0,
      ortalamaSapma: 0,
      stdSapma: 0,
      toleransIcindeYuzde: 100,
      sure,
      vertexSayisiA: 0,
      vertexSayisiB: 0,
      renkSkalasi: { min: 0, max: 0, tolerans: this.options.computationAccuracy },
      tier: 'cpu',
      farkliVertexA: 0,
      farkliVertexB: 0
    };
  }

  dispose(): void {
    this.aborted = true;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════════════════════

let _instance: FikoCadDiffService | null = null;

export function getCadDiffService(): FikoCadDiffService {
  if (!_instance) {
    _instance = new FikoCadDiffService();
  }
  return _instance;
}
